export enum UserRole {
  ADMIN = 'ADMIN',
  EMPLOYEE = 'EMPLOYEE',
  RESPONSIBLE = 'RESPONSIBLE',
  TEACHER = 'TEACHER',
  STUDENT = 'STUDENT',
}

export type RoomAlert = 'NONE' | 'WARNING' | 'CRITICAL';

export enum UserRoleExcelExclusive {
  ALL = 'ALL',
  UNKNOWN = 'UNKNOWN',
  RESPONSIBLE_STUDENT = 'RESPONSIBLE_STUDENT',
}

export type CustomExcelUserRole = UserRole | UserRoleExcelExclusive;

export enum PaymentReason {
  PAY_FOR_CLASSES = 'PAY_FOR_CLASSES',
  ADD_USER_CREDITS = 'ADD_USER_CREDITS',
}

export enum RecurrenceTypes {
  ONCE = 'ONCE',
  NEVER = 'NEVER',
  UNTIL = 'UNTIL',
  COUNT = 'COUNT',
}

export enum DeleteTypes {
  DATE = 'DATE',
  UNTIL = 'UNTIL',
}

export enum PaymentMethod {
  BOLETO = 'BOLETO',
  PIX = 'PIX',
  CASH = 'CASH',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  CANCELED = 'CANCELED',
}

export enum CreditReason {
  DEFAULT = 'DEFAULT',
  ADD_CREDIT_WITH_PAYMENT = 'ADD_CREDIT_WITH_PAYMENT',
  PAID_REPORT_ADJUSTMENT = 'PAID_REPORT_ADJUSTMENT',
  CLASSES_PAYMENT_DISCOUNT = 'CLASSES_PAYMENT_DISCOUNT',
}

export type RegisterEmployeeReq = {
  displayName: string;
  fullName: string;
  email: string;
  notifyEmail: boolean;
  //notifySMS: boolean;
  notifyWhatsapp: boolean;
};

export type RegisterAdminReq = {
  displayName: string;
  fullName: string;
  email: string;
  notifyEmail: boolean;
  //notifySMS: boolean;
  notifyWhatsapp: boolean;
};

export type RegisterStudentReq = {
  displayName: string;
  fullName: string;
  email?: string;
  responsibleId: number;
  phone?: string;
  birthdate: Date;
  studentWithAccess: boolean;
  notifyEmail: boolean;
  //notifySMS: boolean;
  notifyWhatsapp: boolean;
};

export type RegisterResponsibleReq = {
  displayName: string;
  fullName: string;
  email: string;
  notifyEmail: boolean;
  //notifySMS: boolean;
  notifyWhatsapp: boolean;
  cpf: string;
  phone: string;
  postalCode: string;
  addressNumber: string;
  addressComplement?: string;
  hourElementaryChargeValue: number;
  hourUniversityChargeValue: number;
  isStudent: boolean;
  birthdate?: Date;
};

export type TeacherRes = {
  classElementaryValue: number;
  classUniversityValue: number;
  subjectIdsTaught: number[];
};

export type RegisterTeacherReq = {
  displayName: string;
  fullName: string;
  email: string;
  cpf?: string;
  cnpj?: string;
  phone: string;
  birthdate: Date;
  pix?: string;
  bank: string;
  agency: string;
  account: string;
  classElementaryValue: number;
  classUniversityValue: number;
  notifyEmail: boolean;
  //notifySMS: boolean;
  notifyWhatsapp: boolean;
  subjectIdsTaught: number[];
};

export type ResetPasswordReq = {
  password: string;
  confirmPassword: string;
  userId: number | null;
  token: string | null;
};

export type MeRes = {
  id: number;
  email: string;
  fullName: string;
  displayName: string;
  active: true;
  role: UserRole;
  createdAt: Date;
  updatedAt: Date;
};

export type MessageRes = {
  message: string;
};

export type DeleteManySubjectsRes = { count: number };

export type ForgotPasswordReq = {
  email: string;
  resetUrl: string;
};

export type PaymentData = {
  id: number;
  payerName: string;
  payerId: number;
  value: number;
  paidValue?: number;
  discountValue: number;
  invoiceUrl?: string;
  bankSlipUrl?: string;
  paymentDate?: Date;
  method?: PaymentMethod;
  asaasId?: string;
  status: PaymentStatus;
  dueDate: Date;
  reason: PaymentReason;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
};

export type CreditData = {
  id: number;
  receiverId: number;
  receiverName: string;
  giverName?: string;
  giverId?: number;
  value: number;
  currentBalance: number;
  description: string;
  reason: CreditReason;
  billetUrl: string;
  paymentId: number;
  createdAt: Date;
};

export enum CreateCreditOperation {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

export type CreateCreditDto = {
  receiverId: number;
  description: string;
  value: number;
  creditEditionType: CreateCreditOperation;
  dueDate: Date;
  withPaymentBeforeAdd: boolean;
};

export type Responsible = {
  id: number;
  cpf: string;
  phone: string;
  address: string;
  hourElementaryChargeValue: number;
  hourUniversityChargeValue: number;
  isStudent: boolean;
};

export type ResponsibleRes = {
  user: {
    fullName: string;
    email: string;
  };
} & Responsible;

export type AllResponsiblesRes = ({
  user: {
    fullName: string;
    email: string;
  };
} & Responsible)[];

export type Student = {
  responsible: {
    id: number;
    postalCode: string;
    addressComplement: string;
    addressNumber: string;
    isStudent: boolean;
    user?: {
      fullName: string;
    };
  };
};

export type ResponsibleData = {
  isStudent: boolean;
  students?: {
    id?: number;
    user?: {
      fullName: string;
    };
  }[];
};

export type Teacher = {
  subjectsTaught: SubjectEntity[];
};

export type UserRes = {
  id: number;
  email: string;
  fullName: string;
  displayName: string;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
  role: UserRole;
  student: Student | null;
  teacher: Teacher | null;
  responsible: ResponsibleData | null;
};

export type StudentDetailedRes = {
  responsibleId: number;
  phone: string;
  birthdate: Date;
  responsible: {
    fullName: string;
  };
};

export type StudentAddressRes = {
  postalCode: string;
  addressNumber: string;
  addressComplement: string;
};

export type TeacherDetailedRes = {
  cpf: string;
  cnpj: string;
  phone: string;
  birthdate: Date;
  pix: string;
  bank: string;
  agency: string;
  account: string;
  classElementaryValue: number;
  classUniversityValue: number;
  subjectsTaught: number[];
};

export type ResponsibleDetailedRes = {
  asaasId: string;
  cpf: string;
  phone: string;
  hourElementaryChargeValue: number;
  hourUniversityChargeValue: number;
  postalCode: string;
  addressNumber: string;
  addressComplement: string;
  isStudent: boolean;
};

export type AdminDetailedRes = Record<string, never>;

export type EmployeeDetailedRes = Record<string, never>;

export type UserDetailedRes = {
  id: number;
  email: string;
  fullName: string;
  displayName: string;
  active: boolean;
  role: UserRole;
  createdAt: Date;
  updatedAt: Date;
  notifyEmail: boolean;
  notifySMS: boolean;
  notifyWhatsapp: boolean;

  student: StudentDetailedRes;
  teacher: TeacherDetailedRes;
  responsible: ResponsibleDetailedRes;
  admin: AdminDetailedRes;
  employee: EmployeeDetailedRes;
};

export type UpdateUserReq = {
  fullName?: string;
  displayName?: string;
  email?: string;
  notifyEmail?: boolean;
  notifyWhatsapp?: boolean;
  //notifySMS?: boolean;
  role?: UserRole;
  active?: boolean;
  [UserRole.TEACHER]?: {
    phone?: string;
    account?: string;
    agency?: string;
    pix?: string;
    bank?: string;
    classElementaryValue?: number;
    classUniversityValue?: number;
    birthdate?: Date;
    cnpj?: string;
    cpf?: string;
    subjectsTaught?: number[];
  };
  [UserRole.STUDENT]?: {
    studentWithAccess?: boolean;
    phone?: string;
    responsibleId?: number;
    birthdate?: Date;
  };
  [UserRole.RESPONSIBLE]?: {
    cpf?: string;
    phone?: string;
    postalCode?: string;
    province?: string;
    address?: string;
    addressNumber?: string;
    addressComplement?: string;
    hourElementaryChargeValue?: number;
    hourUniversityChargeValue?: number;
    isStudent?: boolean;
    homeClassesPlace?: string;
  };
  [UserRole.ADMIN]?: Record<string, never>;
  [UserRole.EMPLOYEE]?: Record<string, never>;
};

export type AllUserRes = {
  users: UserRes[];
};

export type SubjectEntity = {
  id: number;
  name: string;
  level: Level;
};

export enum Level {
  KINDERGARTEN = 'KINDERGARTEN',
  ELEMENTARY_1 = 'ELEMENTARY_1',
  ELEMENTARY_2 = 'ELEMENTARY_2',
  HIGH_SCHOOL = 'HIGH_SCHOOL',
  UNIVERSITY = 'UNIVERSITY',
  TECHNICAL = 'TECHNICAL',
}

export type RecurrenceRuleReq = {
  recurrenceType: RecurrenceTypes;
  intervalType: IntervalType;
  intervalNumber: number;
  weekdays: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  until?: Date;
  count?: number;
};

export type ReportsDetails = {
  reportId: number;
  status: ReportStatus;
  subjectName: string;
  teacherName: string;
  teacherEmail: string;
  studentName: string;
  responsibleName: string;
  date: Date;
};

export type CreateClassEventReq = {
  eventId?: number; // used to update class
  studentId: number;
  teacherId: number;
  subjectId: number;
  description: string;
  placeType: PlaceType;
  place: string;
  startHour: Date;
  duration: number;
  startDate: Date;
  bypassConflict?: boolean;
  teacherObservations?: string;
  timezone?: string;
  valueFromResponsible: number;
  valueToTeacher: number;
  valueToTrip: number;
  recurrenceRule: RecurrenceRuleReq;
};

export type CreateSubjectReq = {
  name: string;
  levels?: Level[];
};

export type UpdateSubjectReq = {
  name: string;
  levels?: Level[];
};

export type DeleteSubjectReq = {
  ids: number[];
};

export type AllSubjectsRes = SubjectEntity[];

export type ClassEvent = {
  id: number;
  teacherId: number;
  studentId: number;
  subjectId: number;
  isClass: true;
  teacherObservations: string;
  changeValueJustification: string; // Justification for changing default class value
  placeType: PlaceType;
  description: string;
  place: string;
  color: string; //Hex code (e.g., #FF5733) used to visually distinguish the event in the calendar UI
  startHour: Date;
  duration: number;
  startDate: Date;
  endDate: Date;
  rrule: string;
  student: UserRes;
  responsible: UserRes;
  teacher: UserRes;
  subject: SubjectEntity;
  reports?: ReportEntity[];
  valueFromResponsible: number;
  valueToTeacher: number;
  valueToTrip: number;
  createdAt: Date;
  updatedAt: Date;
  userRoleCalendar?: UserRole; // treats the origin of calendar event, being Teacher or Student Used to differentiate the context in which the event appears in the calendar
  roomAlert?: RoomAlert;
};

export type EventDataIds = {
  teacherId: number;
  studentId: number;
  subjectId: number;
};
export type DefaultDataClassEventRes = {
  valueFromResponsible: number;
  valueToTeacher: number;
};

export type TripValueRes = {
  tripValue: number;
};

export type UpdateClassReq = {
  updateClassType: DeleteTypes;
  updateClassDate: Date;
  bypassPaymentClass?: boolean;
} & CreateClassEventReq;

export type UpdateStatusReportReq = {
  status: ReportStatus;
};

export type UpdateReportReq = {
  studentPerformance?: StudentPerformance;
  status?: ReportStatus;
  taughtContent?: string;
  observations?: string;
  wasStudentAbsent?: boolean;
  reviewRequest?: string;
};

export type DisableUserReq = {
  deleteEvents: boolean;
};

export type ReportEntity = {
  id: number;
  classId: number;
  teacherId: number;
  studentId: number;
  date: Date;
  startHour: Date;
  duration: number;
  place: string;
  studentPerformance: StudentPerformance;
  status: ReportStatus;
  taughtContent: string;
  observations: string;
  valueFromResponsible: number;
  valueToTeacher: number;
  valueToTrip: number;
  createdAt: Date;
  updatedAt: Date;
  wasStudentAbsent: boolean;
  reviewRequest: string;
};

export enum ReportStatus {
  PENDING = 'PENDING',
  DONE = 'DONE',
}

export enum StudentPerformance {
  IMPROVED = 'IMPROVED',
  WORSE = 'WORSE',
  SAME = 'SAME',
  FIRST = 'FIRST',
}

export enum PlaceType {
  REMOTE = 'REMOTE',
  STUDENT_HOME = 'STUDENT_HOME',
  PLUS_HEADQUARTER = 'PLUS_HEADQUARTER',
}

export type DeleteEventReq = {
  removeDateRecurrence: DeleteTypes;
  date: Date;
  bypassPaymentClass?: boolean;
};

export type PendingReportsRes = {
  pendingReports: number;
};

export type GetAllUsersParams = {
  role?: UserRole | UserRole[];
  includeDisabled?: boolean;
};

export type ReceiveInCashReq = {
  paymentDate: Date;
  value: number;
  notifyCustomer?: boolean;
};

export type ResponsibleCreditSummaryDto = {
  balance: number;
  pendingPaymentsTotalValue: number;
  predictedMonthlyPaymentTotalValue: number;
  predictedMonthlyPaymentReports: ReportsDetails[];
};

export enum IntervalType {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export type FindReportPaymentsReq = {
  eventDate: Date;
  deleteType: DeleteTypes;
};

export type UpdatePaymentParams = {
  dueDate: Date;
};

export type ClassEventInfoReq = Pick<
  ClassEvent,
  'studentId' | 'teacherId' | 'startDate' | 'endDate' | 'startHour' | 'duration'
> & { rrule: RecurrenceRuleReq; eventId?: number };
